import { addMonths, format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Dropdown,
  DropdownType,
  Grid,
  Label,
  LabelType,
  Pill, useGlobalModalContext,
} from '../_ui';
import { ChargerEV } from '../../assets/icons';
import { DURATION_TYPES, durationTypeValues, getLicenseType } from './utils';
import { useRenewLicenseMutation } from '../../services/license.api';
import { License } from '../../stores/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '../_ui/Tooltip.component';
import { useCompany } from '../../hooks/useCompany';
import { SCOPE_TYPE } from '../../services/utils';
import { Form } from '../_ui/Form.component';
import { useCharger } from '../../hooks/useChargers';

interface LicenseRenewDialogProps {
  license: License;
  onRenew?: (newDuration: number) => void;
}

const LicenseRenewModal = ({ license, onRenew }: LicenseRenewDialogProps) => {
  const { t } = useTranslation();
  const [selectedDuration, setSelectedDuration] = useState(durationTypeValues.ONE_YEAR);
  const [triggerRenewLicense, renewLicenseResult] = useRenewLicenseMutation();
  const { userScope } = useCompany();
  const { getChargerNameById } = useCharger();
  const { hideModal } = useGlobalModalContext();

  const expirationDate = useMemo(() => {
    return addMonths(
      new Date(license.expirationDate ?? (() => { throw new Error('Expiration date is required'); })()),
      selectedDuration,
    );
  }, [license.expirationDate, selectedDuration]);

  const durationDropdownItems = useMemo(() => {
    return Object.entries(DURATION_TYPES).map(([key, value]) => {
      const durationInMonths = durationTypeValues[key as keyof typeof durationTypeValues];
      return {
        id: value,
        label: t(value),
        selected: selectedDuration === durationInMonths,
      };
    });
  }, [selectedDuration, t]);

  const handleRenewLicense = () => {
    if (!license.licenseKey || !license.companyId) return;

    triggerRenewLicense({
      licenseRenewalRequestUrlParam: {
        licenseKey: license.licenseKey,
        companyId: license.companyId,
        scope: userScope || SCOPE_TYPE.COMPANY,
      },
      licenseRenewalRequestBody: {
        duration: selectedDuration,
      },
    });

    onRenew?.(selectedDuration);
  };

  const getChargerLabel = (): string => {
    const chargerId = license.assignedCharger;
    const name = chargerId ? getChargerNameById(chargerId) : '';
    const label = name || t('unassigned');
    return label.length > 10 ? `${label.slice(0, 7)}...${label.slice(-1)}` : label;
  };

  const licenseColumns = [
    {
      key: 'licenseKey',
      title: t('license_key'),
      class: 'w-[25%]',
      component: () => (
        <Tooltip>
          <TooltipTrigger>
            <div className='flex items-center py-1.5'>
              <Label
                text={license.licenseKey}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
                className='truncate max-w-[150px]'
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <Label text={license.licenseKey} type={LabelType.BODY3} color={ColorType.WHITE} />
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      key: 'assignedCharger',
      title: t('charger'),
      class: 'w-[25%]',
      component: () => (
        <Tooltip>
          <TooltipTrigger>
            <div className='flex items-center max-w-[120px] truncate py-1.5'>
              <Pill
                iconLeft={ChargerEV}
                label={getChargerLabel()}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <Label
              text={getChargerNameById(license.assignedCharger || '') || t('unassigned')}
              type={LabelType.BODY3}
              color={ColorType.WHITE}
            />
          </TooltipContent>
        </Tooltip>
      ),
    },
    {
      key: 'type',
      title: t('license_type'),
      class: 'w-[18%]',
      component: () => (
        <Label
          text={t(getLicenseType(license.type))}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
          className='truncate'
        />
      ),
    },
    {
      key: 'ports',
      title: t('ports'),
      class: 'w-[15%]',
      component: () => (
        <Label text={String(license.ports)} type={LabelType.BODY3} color={ColorType.BLACK} />
      ),
    },
    {
      key: 'includes4G',
      title: t('4g_text'),
      class: 'w-[17%]',
      component: () => (
        <Label
          text={license.includes4G ? t('yes') : t('no')}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
        />
      ),
    },
  ];

  return (
    <Form
      onSubmit={handleRenewLicense}
      queryResult={renewLicenseResult}
      onQuerySuccess={() => { hideModal(); }}
      onQueryFailed={() => { hideModal(); }}
    >
      <div className='flex flex-col gap-2 pb-5'>
        <Label text={t('license_renew_msg')} type={LabelType.LABEL_M} color={ColorType.BLACK} />

        <div className='w-full items-center text-left border-y border-grey1'>
          <Grid
            columns={licenseColumns}
            data={[{ ...license, bg: ColorType.WHITE }]}
            primaryKey='licenseKey'
            isLoading={false}
          />
        </div>

        <div className='flex flex-row justify-between mt-2'>
          <div className='flex flex-col gap-2 pr-2'>
            <Label text={t('renewal_term_text')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
            <Dropdown
              placeholder={t('select')}
              placeholderLabelType={LabelType.BODY3_G4}
              headerWidth={188}
              items={durationDropdownItems}
              type={DropdownType.SELECT}
              contentDivWidth={188}
              labelType={LabelType.BODY3}
              labelTypeHighlighted={LabelType.BODY3}
              chevdownIconHighlightColor={ColorType.GREY6}
              headerHighLightClassName='bg-grey1 border-grey3 rounded'
              placement='top-start'
              onItemClick={(item: any) =>
                setSelectedDuration(
                  durationTypeValues[item.id.toUpperCase() as keyof typeof durationTypeValues],
                )
              }
            />
          </div>

          <div className='flex flex-col gap-5 w-1/2 pl-2'>
            <Label
              text={t('new_expiration_date_text')}
              type={LabelType.LABEL_M}
              color={ColorType.BLACK}
            />
            <Label
              text={format(expirationDate, 'MMM d, yyyy')}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          </div>
        </div>

        <div className='mt-6'>
          <Button
            label={t('renew_button_text')}
            type={ButtonType.PRIMARY}
            size={ButtonSize.SMALL_FULL}
            isSumbit
            isLoading={renewLicenseResult.isLoading}
            disabled={renewLicenseResult.isLoading}
          />
        </div>
      </div>
    </Form>
  );
};

LicenseRenewModal.displayName = 'LicenseRenewModal';

export default LicenseRenewModal;
